import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../../../common/helpers/currency";
import type { RawProduct, RawDefaultVariation } from "../../../../../../types";

export interface ProductPriceInfo {
  price: number | undefined;
  regularPrice: number | undefined;
  salePrice: number | undefined;
  formattedPrice: string; // Now potentially empty string if invalid
  formattedRegularPrice: string;
  formattedSalePrice: string;
  formattedMinPrice?: string;
  formattedMaxPrice?: string;
  onSale: boolean;
  salePercentage: number;
}

/**
 * Helper to calculate price info.
 * Handles hiding price if not purchasable and price is 0.
 */
export const getProductPriceInfo = (
  product: RawProduct,
  defaultVariation: RawDefaultVariation | null,
  isPurchasable: boolean
): ProductPriceInfo => {
  const hasDefaultVariation = !!defaultVariation;

  const currencyInfo = extractCurrencyInfo(product.prices);
  const currencyMinorUnit = currencyInfo.currencyMinorUnit ?? 2;

  const rawPrice = hasDefaultVariation
    ? defaultVariation.price
    : product.prices.price;
  const rawRegularPrice = hasDefaultVariation
    ? defaultVariation.regular_price
    : product.prices.regular_price;
  const rawSalePrice = hasDefaultVariation
    ? defaultVariation.sale_price
    : product.prices.sale_price;

  const divisor = Math.pow(10, currencyMinorUnit);
  const price = parseFloat(rawPrice) / divisor;
  const regularPrice = parseFloat(rawRegularPrice) / divisor;
  const salePrice = parseFloat(rawSalePrice) / divisor;

  // --- Hide price if 0 and not purchasable ---
  // If the product is not purchasable (e.g., no price set) and the API returns "0",
  // we should treat the price as undefined/empty to prevent "0.00 ₺" from showing.
  const isValidPrice = isPurchasable || price > 0;

  const onSale = hasDefaultVariation
    ? defaultVariation.on_sale
    : product.on_sale;

  let salePercentage = 0;
  if (onSale && regularPrice > 0 && salePrice < regularPrice) {
    salePercentage = Math.round(
      ((regularPrice - salePrice) / regularPrice) * 100
    );
  }

  let formattedMinPrice: string | undefined = undefined;
  let formattedMaxPrice: string | undefined = undefined;

  // Always extract price range for variable and grouped products,
  // regardless of whether a default variation is set.
  if (
    (product.type === "grouped" || product.type === "variable") &&
    product.prices.price_range
  ) {
    formattedMinPrice = formatPrice(
      product.prices.price_range.min_amount,
      currencyInfo
    );
    formattedMaxPrice = formatPrice(
      product.prices.price_range.max_amount,
      currencyInfo
    );
  }

  return {
    price: isValidPrice ? (isNaN(price) ? undefined : price) : undefined,
    regularPrice: isValidPrice
      ? isNaN(regularPrice)
        ? undefined
        : regularPrice
      : undefined,
    salePrice: isValidPrice
      ? isNaN(salePrice)
        ? undefined
        : salePrice
      : undefined,
    formattedPrice: isValidPrice ? formatPrice(rawPrice, currencyInfo) : "",
    formattedRegularPrice: isValidPrice
      ? formatPrice(rawRegularPrice, currencyInfo)
      : "",
    formattedSalePrice: isValidPrice
      ? formatPrice(rawSalePrice, currencyInfo)
      : "",
    onSale,
    salePercentage,
    formattedMinPrice,
    formattedMaxPrice,
  };
};
